<script setup lang="ts">
    import { computed, ref, watch } from "vue";
    import KDialog from "@/components/generic/KDialog.vue";
    import KButton from "@/components/Button.vue";
    import KForm from "@/components/forms/Form/KForm.vue";
    import KFormFieldDisplay from "@/components/forms/displays/FormFieldDisplay/KFormFieldDisplay.vue";
    import KAutocomplete from "@/components/forms/inputs/Autocomplete/KAutocomplete.vue";
    import KontaktorsakAutocomplete from "@/components/forms/reusables/KontaktorsakAutocomplete.vue";
    import DialogAktivPatient from "@/tabs/Operation/DialogAktivPatient.vue";
    import { fetchData } from "@/tabs/Operation/utils.js";
    import { openDialog, flashMessage } from "@/utils";
    import { fetchProfilesRollAndOrgUnit } from "@/components/forms/reusables/profileHelper";
    import { useStore } from "@/store";
    import { useKForm } from "@/composables/useKForm";
    import { Besoksmall } from "@/tabs/Oppenvard/types.js";
    import { Option } from "@/components/forms/inputs/Autocomplete/types.js";
    import { Besokstyp } from "@/tabs/Oppenvard/types.js";
    import { RollType } from "@/types";
    import { Trigger } from "@/components/forms/Form/types";
    import { RenderDirection } from "@/components/forms/types";
    const store = useStore();

    defineProps<{
        patient: Record<string, string[]> | {};
    }>();

    const emit = defineEmits<{
        (e: "saved"): void;
    }>();

    const { inputErrors, formErrors, parseErrorResponse, setErrors } = useKForm();

    const dialogRef = ref<InstanceType<typeof KDialog>>();
    const formRef = ref();
    const mallAutocompleteRef = ref<InstanceType<typeof KAutocomplete>>();
    const mallar = ref<Besoksmall[]>([]);
    const mallarOptions = ref<Option[]>([]);

    const mallId = ref<Option["value"]>(null);
    const orgUnitId = ref<Option["value"]>(null);

    const showDialog = () => {
        dialogRef.value?.show();
    };

    const vardpersonalAutocompleteRef = ref<InstanceType<typeof KAutocomplete>>();

    const selectedMall = computed<Besoksmall | undefined>(() => mallar.value.find(({ id }) => id == mallId.value));

    const besokstyp = computed<Besokstyp | undefined>(() => selectedMall.value?.typ_display || undefined);

    const rollerByMall = computed<RollType[] | []>(() => selectedMall.value?.roller_display || []);

    const rollerCodesByMall = computed<string[]>(() => rollerByMall.value.map((r) => r.code));

    const currentPatientId = computed(() => store.getters["patientData/currentId"]);

    const submitForm = async (skapaKallelse: boolean = false) => {
        let formData = await formRef.value?.manualSubmit();
        if (!formData) {
            return;
        }

        formData = {
            ...formData,
            vardpersonal: formData.vardpersonal ? [formData.vardpersonal] : [], // wrappa vårdnadspersonal i array
            patient: currentPatientId.value,
        };

        try {
            await store.dispatch("oppenvardsbesok/direktboka", { payload: formData });
            flashMessage("Besök bokat");
            if (skapaKallelse) {
                //   await openKallelse();
            }
            emit("saved");
            hideDialog();
        } catch (error: any) {
            let errors = parseErrorResponse(error);
            if (errors) {
                setErrors(errors);
            }
        }
    };

    const initForm = async () => {
        try {
            const result = await fetchData({
                url: "/oppenvard/besoksmallar/",
                params: { aktiv: true },
            });

            if (result?.length) {
                mallar.value = result;
                mallarOptions.value = result.map(({ id, namn }: Besoksmall) => ({
                    value: String(id),
                    label: namn,
                }));
                mallAutocompleteRef?.value?.setOptions({
                    options: mallarOptions.value,
                    setDefault: true,
                });
            }
        } catch (error) {
            openDialog("Kunde inte hämta besöksmallar", "error");
        }

        return {
            vantelistdatum: new Date().toLocaleDateString("sv-SE"),
        };
    };

    const hideDialog = () => {
        dialogRef.value?.hide();
    };

    const config = {
        skickaVantelistaSize: "secondary",
        skickaVantelistaVariant: "standard",
        submitSize: "secondary",
        submitVariant: "standard",
        cancelSize: "slim",
        cancelVariant: "contour",
        bokaSkapaKallelseSize: "slim",
        bokaSkapaKallelseVariant: "standard",
    };

    const texts = {
        headerTitle: "Direktboka",
        submitContent: "Boka",
        cancelContent: "Avbryt",
        bokaSkapaKallelseContent: "Boka och skapa kallelse",
    };

    const disableSubmit = false;
    const disableBokaSkapaKallelse = false;

    const onSelectMall = async ({ value }: { value: any }) => {
        mallId.value = value;
    };

    const onSelectOrgUnit = async ({ value }: { value: any }) => {
        if (rollerCodesByMall.value.length) {
            orgUnitId.value = value;
            fetchProfiles();
        }
    };

    watch(rollerByMall, () => {
        fetchProfiles();
    });

    const fetchProfiles = async () => {
        if (!mallId.value) {
            return;
        }
        const params: Record<string, any> = {
            orgUnit__only_permitted: true,
            orgUnit: orgUnitId.value,
            isActive: true,
        };

        if (rollerCodesByMall.value.length) {
            params["roll__code__in"] = rollerCodesByMall.value.join(",");
        }

        try {
            const profiles = await fetchProfilesRollAndOrgUnit({
                params,
                showRoll: "yes",
                showOrgUnit: "no",
            });
            vardpersonalAutocompleteRef?.value?.setOptions?.({ options: profiles });
        } catch (error) {
            console.error("Error fetching profiles");
        }
    };

    const formTriggers: Trigger[] = [
        {
            fieldName: "vardpersonal",
            dependentOn: ["mall", "enhet"],
            action: ({ dependentOnValue }: { dependentOnValue: any }) => {
                return {
                    disabled: !dependentOnValue["mall"] || !dependentOnValue["enhet"],
                    value: null,
                };
            },
        },
        {
            fieldName: "besokslangd",
            dependentOn: "mall",
            action: ({ dependentOnValue }: { dependentOnValue: any }) => {
                const selectedMall = mallar.value.find((mall) => mall.id == dependentOnValue);
                return {
                    value: selectedMall?.standardtid || null,
                };
            },
        },
    ];

    defineExpose({
        show: showDialog,
    });
</script>

<template>
    <KDialog
        ref="dialogRef"
        width="medium"
        cancel-size="slim"
        :closable="true"
        :closeOnBackdropClick="false"
        :headerTitle="texts.headerTitle"
    >
        <template #default>
            <KForm
                ref="formRef"
                id="boka-direkt-form"
                :triggers="formTriggers"
                :initial-values-getter="initForm"
                :form-errors="formErrors"
                :errors="inputErrors"
            >
                <template #default>
                    <div class="form-content">
                        <div class="patient-dialog-container">
                            <DialogAktivPatient :patient="patient" />
                        </div>
                        <KSection name="besokdetaljer" heading="Besöksdetaljer" :collapsible="false" :numbered="false">
                            <KontaktorsakAutocomplete />
                            <KAutocomplete
                                ref="mallAutocompleteRef"
                                @inputValue="onSelectMall"
                                name="mall"
                                label="Besök"
                                validation="required"
                            />
                            <KFormFieldDisplay
                                name="besokstyp"
                                label="Besökstyp"
                                :value="besokstyp?.namn ?? ''"
                                :showEmptyValue="true"
                            />
                            <KFormFieldDisplay
                                name="roller"
                                label="Roll"
                                :value="rollerByMall?.map((r) => r.displayName)"
                                :showEmptyValue="true"
                                :contentDirection="RenderDirection.Vertical"
                            />
                            <OrgunitAutocomplete
                                name="enhet"
                                @inputValue="onSelectOrgUnit"
                                label="Enhet"
                                :urlParams="{
                                    vardenhetstyp: 'oppenvardsmottagning',
                                    is_active: true,
                                    only_permitted: true,
                                }"
                                validation="required"
                            />
                            <ProfileAutocomplete
                                ref="vardpersonalAutocompleteRef"
                                name="vardpersonal"
                                label="Vårdpersonal"
                                validation="required"
                                :retrieveOptionsOnMount="false"
                                show-roll="yes"
                            />
                            <KDateTimepicker name="vantelistdatum" label="Väntelistdatum" validation="required" />
                            <AvtalAutocomplete />
                            <KText name="kommentar" label="Kommentar" type="textarea" />
                        </KSection>
                        <KSection name="bokning" heading="Bokning" :collapsible="false" :numbered="false">
                            <KDateTimepicker name="bokat_datum" label="Datum" validation="required" />
                            <KDateTimepicker
                                name="bokad_tid"
                                type="time"
                                convertTo="time"
                                label="Tid"
                                validation="required"
                            />

                            <KNumber name="besokslangd" label="Besökslängd (min)" :minValue="0" validation="required" />
                        </KSection>
                    </div>
                </template>
                <template #actions>
                    <KButton
                        :disabled="disableSubmit"
                        type="submit"
                        :size="config.submitSize"
                        :variant="config.submitVariant"
                        @click="submitForm"
                    >
                        {{ texts.submitContent }}
                    </KButton>
                    <KButton
                        :disabled="disableBokaSkapaKallelse"
                        type="submit"
                        :size="config.bokaSkapaKallelseSize"
                        :variant="config.bokaSkapaKallelseVariant"
                        @click="() => submitForm(true)"
                    >
                        {{ texts.bokaSkapaKallelseContent }}
                    </KButton>
                    <KButton
                        v-if="texts.cancelContent"
                        name="cancel"
                        type="button"
                        :size="config.cancelSize"
                        :variant="config.cancelVariant"
                        @click="hideDialog"
                    >
                        {{ texts.cancelContent }}
                    </KButton>
                </template>
            </KForm>
        </template>
    </KDialog>
</template>

<style lang="scss" scoped>
    @import "@/style/_variables.scss";

    .error-messages {
        list-style-type: none;
        padding-inline-start: 0;
        margin-bottom: 0;
    }

    .message p {
        color: $color-text-popup;
        font-size: 28px;
        font-weight: 500;
        line-height: 31px;
    }
</style>
